/**
 * THE COMPANY — BRANCHES, FINANCIAL YEARS AND DOCUMENT NUMBERING.
 *
 * Three settings that are changed rarely and matter enormously when they are.
 *
 * A financial year has to be OPEN before a document in it can be numbered, so
 * the year that opens on 1 April is somebody's job on 31 March, and a screen
 * that hides it is the reason a challan cannot be issued that morning.
 *
 * A number series is NEVER editable here. It is where the next challan number
 * comes from, and a series that can be wound back is a series that can issue
 * two documents with one number. It is shown, because a manager needs to know
 * what the last number was, and it is shown as a fact rather than a field —
 * the database refuses to move it and this screen does not pretend otherwise.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun } from '../hooks.js';
import { Banner, Button, Card, Loading, Pill, Register, Select, Text, TextArea, Check, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { count, date, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('company')!.messages;

export function Company({ session }: { session: Session }) {
  const [tab, setTab] = useState<'branches' | 'calendar' | 'letterhead'>('branches');
  return (
    <Page title="The company" sub="Branches and plants, financial years, and where document numbers come from.">
      <div className="segmented">
        <button type="button" className={tab === 'branches' ? 'on' : ''} onClick={() => setTab('branches')}>Branches and plants</button>
        <button type="button" className={tab === 'calendar' ? 'on' : ''} onClick={() => setTab('calendar')}>Years and numbering</button>
        <button type="button" className={tab === 'letterhead' ? 'on' : ''} onClick={() => setTab('letterhead')}>Letterhead and mark</button>
      </div>
      {tab === 'branches' ? <Branches session={session} />
        : tab === 'letterhead' ? <Letterhead session={session} />
        : <><Calendar session={session} /><RentRule session={session} /></>}
    </Page>
  );
}

function Branches({ session }: { session: Session }) {
  const [q, setQ] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [opening, setOpening] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('branches.list', {
    ...(q.trim() ? { q: q.trim() } : {}), ...(onlyActive ? { active: true } : {}), page, pageSize: 50,
  }), [q, onlyActive, page], MESSAGES);
  const may = can(session, 'branch.manage');

  const columns: readonly Column<{ branchId: number; code: string; name: string; kind: string; address: string | null; stateCode: string | null; gstin: string | null; active: boolean; openedOn: string | null; closedOn: string | null }>[] = [
    { key: 'code', head: 'Code', cell: (r) => <strong>{r.code}</strong> },
    { key: 'name', head: 'Name', cell: (r) => <>{r.name}{r.address ? <div className="muted">{r.address}</div> : null}</> },
    { key: 'kind', head: 'What it is', cell: (r) => <Pill>{r.kind}</Pill> },
    { key: 'state', head: 'State', cell: (r) => r.stateCode ?? <span className="muted">—</span> },
    { key: 'gstin', head: 'GSTIN', cell: (r) => r.gstin ?? <span className="muted">—</span> },
    { key: 'opened', head: 'Opened', cell: (r) => date(r.openedOn) },
    { key: 'status', head: 'Status', cell: (r) => r.active ? <Pill tone="good">open</Pill> : <Pill tone="muted">{`closed ${date(r.closedOn)}`}</Pill> },
    { key: 'edit', head: '', cell: (r) => may && r.active ? <Button small onClick={() => setEditing((v) => v === r.branchId ? null : r.branchId)}>{editing === r.branchId ? 'Close' : 'Correct'}</Button> : null },
  ];

  return (
    <>
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="q">Search</label>
          <input id="q" type="text" value={q} placeholder="Code or name" onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
        </div>
        <Check name="only-open" checked={onlyActive} onChange={setOnlyActive} label="Only open ones" />
        {may ? <Button kind="primary" onClick={() => setOpening((v) => !v)}>{opening ? 'Cancel' : 'Open a branch'}</Button> : null}
      </div>

      {opening ? <OpenBranch onDone={() => { setOpening(false); asked.reload(); }} /> : null}

      <Card>
        {asked.loading ? <Loading what="branches" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No branch yet. A cylinder stands in a yard and a challan is issued from a place, so nothing can be dispatched until there is one. Open a branch above — a plant if it fills cylinders, a depot if it only holds them." />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>

      {editing !== null && asked.data ? (
        <EditBranch branch={asked.data.rows.find((r) => r.branchId === editing)!} onDone={() => { setEditing(null); asked.reload(); }} />
      ) : null}
    </>
  );
}

function OpenBranch({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('branch');
  const [address, setAddress] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [gstin, setGstin] = useState('');
  const [openedOn, setOpenedOn] = useState(today());
  const runner = useRun(MESSAGES);

  async function submit() {
    const ok = await runner.run(() => api.command('branch.open', {
      code: code.trim(), name: name.trim(), kind: kind as 'branch' | 'plant' | 'depot',
      ...(address.trim() ? { address: address.trim() } : {}),
      ...(stateCode.trim() ? { stateCode: stateCode.trim() } : {}),
      ...(gstin.trim() ? { gstin: gstin.trim() } : {}),
      ...(openedOn ? { openedOn } : {}),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="Open a branch" sub="a plant fills cylinders; a depot holds them; a branch does business">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="branch-code" label="Code" value={code} onChange={setCode} required autoFocus />
          <Text name="branch-name" label="Name" value={name} onChange={setName} required />
          <Select name="branch-kind" label="What it is" value={kind} onChange={setKind}
                  options={[{ value: 'branch', label: 'Branch' }, { value: 'plant', label: 'Plant' }, { value: 'depot', label: 'Depot' }]} />
        </div>
        <div>
          <TextArea name="branch-address" label="Address" value={address} onChange={setAddress} />
          <Text name="branch-state" label="State code" value={stateCode} onChange={setStateCode} help="two characters" />
          <Text name="branch-gstin" label="GSTIN" value={gstin} onChange={setGstin} />
          <Text name="branch-opened" label="Opened on" type="date" value={openedOn} onChange={setOpenedOn} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !code.trim() || !name.trim()}>
        {runner.busy ? 'Opening…' : 'Open it'}
      </Button>
    </Card>
  );
}

function EditBranch({ branch, onDone }: {
  branch: { branchId: number; code: string; name: string; kind: string; address: string | null; stateCode: string | null; gstin: string | null };
  onDone: () => void;
}) {
  const [name, setName] = useState(branch.name);
  const [kind, setKind] = useState(branch.kind);
  const [address, setAddress] = useState(branch.address ?? '');
  const [stateCode, setStateCode] = useState(branch.stateCode ?? '');
  const [gstin, setGstin] = useState(branch.gstin ?? '');
  const [closing, setClosing] = useState(false);
  const [closedOn, setClosedOn] = useState(today());
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  const changes = {
    ...(name.trim() !== branch.name ? { name: name.trim() } : {}),
    ...(kind !== branch.kind ? { kind: kind as 'branch' | 'plant' | 'depot' } : {}),
    ...(address.trim() !== (branch.address ?? '') ? { address: address.trim() || null } : {}),
    ...(stateCode.trim() !== (branch.stateCode ?? '') ? { stateCode: stateCode.trim() || null } : {}),
    ...(gstin.trim() !== (branch.gstin ?? '') ? { gstin: gstin.trim() || null } : {}),
  };
  const nothingChanged = Object.keys(changes).length === 0;

  async function save() {
    const ok = await runner.run(() => api.command('branch.update', { branchId: branch.branchId, changes, reason: reason.trim() }));
    if (ok) onDone();
  }
  async function close() {
    const ok = await runner.run(() => api.command('branch.close', { branchId: branch.branchId, closedOn, reason: reason.trim() }));
    if (ok) onDone();
  }

  return (
    <Card title={`Correct ${branch.code}`} sub="the code is never changed — documents already carry it">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="edit-branch-name" label="Name" value={name} onChange={setName} required />
          <Select name="edit-branch-kind" label="What it is" value={kind} onChange={setKind}
                  options={[{ value: 'branch', label: 'Branch' }, { value: 'plant', label: 'Plant' }, { value: 'depot', label: 'Depot' }]} />
          <Text name="edit-branch-state" label="State code" value={stateCode} onChange={setStateCode} />
        </div>
        <div>
          <TextArea name="edit-branch-address" label="Address" value={address} onChange={setAddress} />
          <Text name="edit-branch-gstin" label="GSTIN" value={gstin} onChange={setGstin} />
          <TextArea name="edit-branch-reason" label="Why it is changing" value={reason} onChange={setReason} />
        </div>
      </div>
      <div className="toolbar">
        <Button kind="primary" onClick={() => { void save(); }} disabled={runner.busy || nothingChanged || !reason.trim()}>
          {nothingChanged ? 'Nothing has changed' : 'Save the correction'}
        </Button>
        <Button kind="danger" onClick={() => setClosing((v) => !v)}>{closing ? 'Keep it open' : 'Close this branch'}</Button>
      </div>
      {closing ? (
        <>
          <Banner kind="warn" title="A closed branch keeps its history and takes no new work."
                  detail="Cylinders recorded there must be moved first — the command refuses otherwise, and says so." />
          <Text name="branch-closed-on" label="Closed on" type="date" value={closedOn} onChange={setClosedOn} required />
          <Button kind="danger" onClick={() => { void close(); }} disabled={runner.busy || !reason.trim()}>
            {runner.busy ? 'Closing…' : 'Close it'}
          </Button>
        </>
      ) : null}
    </Card>
  );
}

function Calendar({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('calendar.get', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const [opening, setOpening] = useState(false);
  const [fy, setFy] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [closingMonth, setClosingMonth] = useState('');
  const [closingYear, setClosingYear] = useState('');
  const [reason, setReason] = useState('');
  const may = can(session, 'calendar.manage');
  // DEF-152 — reopening a settled month is its own right (`period.reopen`,
  // seeded by 0013 and unclaimed until now), not something everyone who may
  // close a month may also undo.
  const canReopen = can(session, 'period.reopen');

  if (asked.loading) return <Card title="Years and numbering"><Loading what="the calendar" /></Card>;
  if (asked.error) return <Card title="Years and numbering"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const d = asked.data!;

  async function openYear() {
    const ok = await runner.run(() => api.command('calendar.openFinancialYear', { fy: fy.trim(), startDate, endDate }));
    if (ok) { setOpening(false); setFy(''); asked.reload(); }
  }
  async function reopenMonth() {
    const ok = await runner.run(() => api.command('calendar.reopenPeriod', { year: Number(closingYear), month: Number(closingMonth), reason: reason.trim() }));
    if (ok) { setClosingMonth(''); setReason(''); asked.reload(); }
  }
  async function closeMonth() {
    const ok = await runner.run(() => api.command('calendar.closePeriod', { year: Number(closingYear), month: Number(closingMonth), reason: reason.trim() }));
    if (ok) { setClosingMonth(''); setReason(''); asked.reload(); }
  }

  return (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      <div className="grid two">
        <Card title="Financial years" sub="a document cannot be numbered in a year that is not open"
              actions={may ? <Button small kind="primary" onClick={() => setOpening((v) => !v)}>{opening ? 'Cancel' : 'Open a year'}</Button> : null}>
          {opening ? (
            <>
              <Text name="fy" label="Financial year" value={fy} onChange={setFy} required help="five characters, e.g. 26-27" />
              <Text name="fy-start" label="First day" type="date" value={startDate} onChange={setStartDate} required />
              <Text name="fy-end" label="Last day" type="date" value={endDate} onChange={setEndDate} required />
              <Button kind="primary" onClick={() => { void openYear(); }} disabled={runner.busy || !fy.trim() || !startDate || !endDate}>
                {runner.busy ? 'Opening…' : 'Open it'}
              </Button>
            </>
          ) : null}
          <Register
            columns={[
              { key: 'fy', head: 'Year', cell: (y: { fy: string }) => <strong>{y.fy}</strong> },
              { key: 'from', head: 'From', cell: (y: { startDate: string }) => date(y.startDate) },
              { key: 'to', head: 'To', cell: (y: { endDate: string }) => date(y.endDate) },
            ]}
            rows={d.years} empty="No financial year is open. Nothing can be numbered until one is." />
        </Card>

        <Card title="Where the next number comes from"
              sub="read only, and deliberately: a series that can be wound back is a series that can issue one number twice">
          <Register
            columns={[
              { key: 'doc', head: 'Document', cell: (s2: { docType: string }) => words(s2.docType) },
              { key: 'fy', head: 'Year', cell: (s2: { fy: string }) => s2.fy },
              { key: 'last', head: 'Last issued', right: true, cell: (s2: { lastValue: number }) => count(s2.lastValue) },
            ]}
            rows={d.series} empty="Nothing has been numbered yet in any open year." />
          <p className="muted" style={{ marginBottom: 0 }}>
            Numbers are given out one at a time and are never re-used. If a number is wrong,
            the document is cancelled and reissued — it is never renumbered.
          </p>
        </Card>
      </div>

      {may ? (
        <Card title="Close a month" sub="a closed month takes no more money — no charge, no bill, no receipt dated into it">
          <div className="grid two">
            <div>
              <Text name="close-year" label="Year" type="number" value={closingYear} onChange={setClosingYear} required />
              <Select name="close-month" label="Month" value={closingMonth} onChange={setClosingMonth} placeholder="Choose the month"
                      options={['January','February','March','April','May','June','July','August','September','October','November','December']
                        .map((m, i) => ({ value: String(i + 1), label: m }))} />
            </div>
            <div>
              <TextArea name="close-reason" label="Why it is being closed, or reopened" value={reason} onChange={setReason} />
              <Banner kind="warn" title="Send that month's bills before you close it."
                      detail="Closing is refused while any of its monthly bills are still drafts, and names whose. Afterwards that month's figures cannot change. Late paperwork is not lost: a trip or a charge dated into a closed month is still recorded with its true date, and its money goes onto the next open month's bill saying which month it is for. Reopen the month only when you want it back in that month itself." />
            </div>
          </div>
          <Button kind="danger" onClick={() => { void closeMonth(); }} disabled={runner.busy || !closingYear || !closingMonth || !reason.trim()}>
            {runner.busy ? 'Closing…' : 'Close that month'}
          </Button>
          {canReopen ? (
            <Button kind="ghost" onClick={() => { void reopenMonth(); }} disabled={runner.busy || !closingYear || !closingMonth || !reason.trim()}>
              {runner.busy ? 'Reopening…' : 'Reopen that month'}
            </Button>
          ) : null}
        </Card>
      ) : null}
    </>
  );
}

/**
 * WHEN HOLDING RENT STARTS (0146, DEF-159/OD-018).
 *
 * D14 always said rent runs from DISPATCH; the code charged from DELIVERY. The correction is
 * PROSPECTIVE and the date is a business decision, so nothing defaults it and nothing is
 * backdated — a trip that has already departed keeps the rule it departed under and no bill is
 * restated. That is the whole of OD-018, and it is said on the screen rather than discovered
 * by being refused.
 *
 * It is here, beside the years and the number series, because it is a company setting of the
 * same kind: set once, read by everything, and consequential.
 */
function RentRule({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('calendar.rentPolicy', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const [starting, setStarting] = useState(false);
  const [from, setFrom] = useState(today());
  const [why, setWhy] = useState('');
  const may = can(session, 'billing.activate_rent_policy');

  if (asked.loading) return <Card title="When holding starts"><Loading what="the rent rule" /></Card>;
  if (asked.error) {
    return <Card title="When holding starts">
      <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
    </Card>;
  }
  const d = asked.data!;
  const onDeparture = d.active?.policy === 'departure_based';
  const decided = onDeparture || d.pending?.policy === 'departure_based';

  async function activate() {
    const ok = await runner.run((retryKey) => api.command('billing.activateRentPolicy', { effectiveFrom: from, reason: why.trim() }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setStarting(false); setWhy(''); asked.reload(); }
  }

  return (
    <Card className="rent-rule" title="When holding starts"
          sub="whether a customer's holding charge runs from the day the lorry leaves, or the day the cylinders arrive"
          actions={may && !decided
            ? <Button kind="primary" onClick={() => setStarting((v) => !v)}>{starting ? 'Leave it as it is' : 'Start it from a date'}</Button>
            : null}>
      <Banner kind={decided ? 'good' : 'info'} title={d.todaySays}
              detail={decided
                ? 'Trips that departed before that date keep the delivery-based rule they departed under, and no bill was restated.'
                : 'Starting it is prospective: it changes what trips leaving on or after the date you choose will charge, and nothing that has already happened.'} />

      {starting ? (
        <div className="grid two">
          <div>
            <Text name="rent-from" label="Starts from" type="date" value={from} onChange={setFrom} required
                  help="today or later — a rule cannot start in the past, because those trips have already departed" />
          </div>
          <div>
            <TextArea name="rent-why" label="Why" value={why} onChange={setWhy}
                      help="kept with the change: somebody will ask next year why it starts on this date" />
            <Button kind="primary" onClick={() => { void activate(); }} disabled={runner.busy || !from || !why.trim()}>
              {runner.busy ? 'Starting…' : 'Start holding at departure'}
            </Button>
          </div>
        </div>
      ) : null}
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}

      {d.history.length > 0 ? (
        <Register
          columns={[
            { key: 'rule', head: 'Rule', cell: (r: { policy: string }) => (
                <Pill tone={r.policy === 'departure_based' ? 'good' : 'muted'}>
                  {r.policy === 'departure_based' ? 'from departure' : 'from delivery'}</Pill>) },
            { key: 'from', head: 'From', cell: (r: { effectiveFrom: string }) => date(r.effectiveFrom) },
            { key: 'why', head: 'Why', cell: (r: { reason: string }) => r.reason },
            { key: 'when', head: 'Recorded', cell: (r: { activatedAt: string }) => date(r.activatedAt) },
          ]}
          rows={d.history} empty="" />
      ) : null}
    </Card>
  );
}

/**
 * WHAT THE COMPANY'S PAPERS SAY IT IS (0176, 0177).
 *
 * The line under its name, its CIN and its website — which the offer's footer
 * prints — and the MARK its cylinder labels carry. The mark is an SVG file:
 * open it in a text editor and paste what is inside, or choose the file and
 * the browser reads it. It is shown exactly as a label will show it, because
 * the only useful preview of a 50 x 25 mm sticker is at that size.
 */
function Letterhead({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('company.letterhead', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const may = can(session, 'branch.manage');
  const [tagline, setTagline] = useState<string | null>(null);
  const [cin, setCin] = useState<string | null>(null);
  const [website, setWebsite] = useState<string | null>(null);
  const [mark, setMark] = useState<string | null>(null);
  // 0180 — where a customer sends the money. It prints on the bill, and until it is set
  // the bill prints without the block rather than with four blank lines.
  const [bankName, setBankName] = useState<string | null>(null);
  const [bankAccount, setBankAccount] = useState<string | null>(null);
  const [bankIfsc, setBankIfsc] = useState<string | null>(null);
  const [bankUpi, setBankUpi] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  if (asked.loading) return <Card title="Letterhead and mark"><Loading what="the company" /></Card>;
  if (asked.error) return <Card title="Letterhead and mark"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const d = asked.data!;
  const v = { tagline: tagline ?? d.tagline ?? '', cin: cin ?? d.cin ?? '', website: website ?? d.website ?? '', mark: mark ?? d.markSvg ?? '',
              bankName: bankName ?? d.bankName ?? '', bankAccount: bankAccount ?? d.bankAccount ?? '',
              bankIfsc: bankIfsc ?? d.bankIfsc ?? '', bankUpi: bankUpi ?? d.bankUpi ?? '' };

  async function save() {
    const ok = await runner.run(() => api.command('company.saveLetterhead', {
      tagline: v.tagline.trim(), cin: v.cin.trim(), website: v.website.trim(), logoSvg: v.mark.trim(),
      bankName: v.bankName.trim(), bankAccount: v.bankAccount.trim(),
      bankIfsc: v.bankIfsc.trim().toUpperCase(), bankUpi: v.bankUpi.trim(),
    }));
    if (ok) { setTouched(false); asked.reload(); }
  }

  return (
    <Card title="Letterhead and mark" sub={`${d.legalName} · trading as ${d.tradingName}`}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="co-tagline" label="The line under the name" value={v.tagline} onChange={(x) => { setTouched(true); setTagline(x); }}
                help="printed in the footer of every offer — e.g. Engineering Industrial Gas Solutions" />
          <Text name="co-cin" label="CIN" value={v.cin} onChange={(x) => { setTouched(true); setCin(x); }} help="printed beside the GSTIN" />
          <Text name="co-website" label="Website" value={v.website} onChange={(x) => { setTouched(true); setWebsite(x); }} help="e.g. kigl.co.in" />
          <div className="k" style={{ marginTop: 14 }}>Where customers send the money</div>
          <p className="muted" style={{ marginTop: 2 }}>
            Printed at the foot of every bill, as CTS1 prints it. Leave it empty and the bill
            simply omits the block — a bill that says what is owed and not where to pay it is
            one the office then explains by telephone.
          </p>
          <Text name="co-bank" label="Bank" value={v.bankName} onChange={(x) => { setTouched(true); setBankName(x); }} help="e.g. State Bank of India, Guwahati" />
          <Text name="co-acct" label="Account number" value={v.bankAccount} onChange={(x) => { setTouched(true); setBankAccount(x); }} />
          <Text name="co-ifsc" label="IFSC" value={v.bankIfsc} onChange={(x) => { setTouched(true); setBankIfsc(x); }} help="four letters, a zero, then six more — e.g. SBIN0001234" />
          <Text name="co-upi" label="UPI" value={v.bankUpi} onChange={(x) => { setTouched(true); setBankUpi(x); }} help="optional — the handle customers can pay to" />
          <p className="muted">
            The address a scanned label goes to is this company’s hostname, {d.hostname ?? 'which is not set'}.
            A label is glued on for twenty years, so it is not guessed and not edited here.
          </p>
        </div>
        <div>
          <div className="field">
            <label htmlFor="co-mark">The mark on its labels (SVG)</label>
            <textarea id="co-mark" rows={6} value={v.mark} placeholder="&lt;svg …&gt;…&lt;/svg&gt;"
                      onChange={(e: { target: { value: string } }) => { setTouched(true); setMark(e.target.value); }} />
            <div className="help">Open the .svg in a text editor and paste what is inside, or choose the file below. A mark carrying script is refused.</div>
          </div>
          <input type="file" accept=".svg,image/svg+xml" aria-label="Choose an SVG file"
                 onChange={(e: { target: { files: { text: () => Promise<string> }[] | null } }) => {
                   const f = e.target.files?.[0];
                   if (f) void f.text().then((t) => { setTouched(true); setMark(t); });
                 }} />
          {v.mark.trim() ? (
            <div style={{ marginTop: 12 }}>
              <div className="k">As a label will show it</div>
              <img alt="" style={{ maxWidth: '20mm', maxHeight: '5mm', display: 'block', marginTop: 6, background: '#fff' }}
                   src={`data:image/svg+xml;utf8,${encodeURIComponent(v.mark)}`} />
            </div>
          ) : <p className="muted">No mark yet — labels print the number and the gas alone.</p>}
        </div>
      </div>
      {may ? (
        <Button kind="primary" disabled={!touched || runner.busy} onClick={() => { void save(); }}>
          {runner.busy ? 'Saving…' : 'Save the letterhead'}
        </Button>
      ) : <p className="muted">Changing this needs the permission that opens a branch.</p>}
    </Card>
  );
}
